import { useState } from "react";
import type { User, UserRole } from "@/mocks/users";
import { rolePrivileges, mockDepartmentsList } from "@/mocks/users";

interface UserModalProps {
  mode: "add" | "edit";
  user?: User | null;
  onClose: () => void;
  onSave: (user: User) => void;
}

export default function UserModal({ mode, user, onClose, onSave }: UserModalProps) {
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [department, setDepartment] = useState(user?.department || "");
  const [role, setRole] = useState<UserRole>(user?.role || "Officer");
  const [status, setStatus] = useState<"Active" | "Inactive">(user?.status || "Active");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !department) return;

    const savedUser: User = {
      id: user?.id || `usr-${Date.now()}`,
      name: name.trim(),
      email: email.trim(),
      department,
      role,
      status,
      lastActive: user?.lastActive || new Date().toISOString(),
      avatar: name
        .trim()
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2),
    };
    onSave(savedUser);
  };

  const privileges = rolePrivileges[role];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-white rounded-lg w-full max-w-[560px] mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-200">
          <h2 className="text-foreground-800 text-lg font-semibold">
            {mode === "add" ? "Add New User" : "Edit User"}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-foreground-700 text-sm font-medium mb-1.5">Full Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Thabo Molefe"
                className="form-input"
                required
              />
            </div>
            <div>
              <label className="block text-foreground-700 text-sm font-medium mb-1.5">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e.g. thabo.molefe@dha.gov.za"
                className="form-input"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-foreground-700 text-sm font-medium mb-1.5">Department</label>
              <select
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="form-input cursor-pointer"
                required
              >
                <option value="">Select department</option>
                {mockDepartmentsList.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-foreground-700 text-sm font-medium mb-1.5">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as "Active" | "Inactive")}
                className="form-input cursor-pointer"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Role / Privilege Assignment */}
          <div>
            <label className="block text-foreground-700 text-sm font-medium mb-2">Role & Privileges</label>
            <div className="grid grid-cols-2 gap-3">
              {(["Officer", "SCM Manager", "CFO/HOD", "System Admin"] as UserRole[]).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRole(r)}
                  className={`text-left px-4 py-3 rounded-md border transition-colors cursor-pointer ${
                    role === r
                      ? "border-primary-500 bg-primary-50 ring-1 ring-primary-500/20"
                      : "border-background-300 hover:border-background-400"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2 h-2 rounded-full ${
                      r === "System Admin" ? "bg-accent-500" :
                      r === "CFO/HOD" ? "bg-primary-500" :
                      r === "SCM Manager" ? "bg-secondary-500" :
                      "bg-background-400"
                    }`}></span>
                    <span className="text-foreground-800 text-sm font-semibold">{r}</span>
                  </div>
                  <p className="text-foreground-500 text-xs">
                    {r === "System Admin" && "Full access across all modules"}
                    {r === "CFO/HOD" && "Reports, approvals, department oversight"}
                    {r === "SCM Manager" && "Contract & review management"}
                    {r === "Officer" && "View contracts, submit reviews"}
                  </p>
                </button>
              ))}
            </div>

            {/* Privilege breakdown */}
            <div className="mt-3 bg-background-50 rounded-md p-3 border border-background-200">
              <p className="text-foreground-500 text-xs font-medium mb-2">
                Permissions for <span className="text-foreground-700">{role}</span>
              </p>
              <div className="flex flex-wrap gap-1.5">
                {privileges.map((p) => (
                  <span
                    key={p}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-white border border-background-200 text-foreground-600"
                  >
                    <i className="ri-check-line text-[10px] text-secondary-600"></i>
                    {p}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-background-200">
            <button
              type="button"
              onClick={onClose}
              className="h-9 px-4 rounded-md text-sm font-medium text-foreground-600 border border-background-300 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="h-9 px-5 bg-primary-600 text-white text-sm font-semibold rounded-md hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              {mode === "add" ? "Add User" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}